import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CreditCard, RefreshCw, Landmark, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const METHOD_LABELS = { card: 'Card', ach: 'ACH', us_bank_account: 'ACH' };

export default function ElectronicPaymentsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ payments: [], configured: true, totals: { gross: 0, fees: 0, net: 0 } });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/stripe/electronic-payments');
      setData(res.data || { payments: [], configured: true, totals: { gross: 0, fees: 0, net: 0 } });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error loading electronic payments');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { payments, configured, totals } = data;

  return (
    <div data-testid="electronic-payments-page" className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" /> Electronic Payments
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Card &amp; ACH collections — fees, net amount, and estimated deposit date, straight from Stripe.</p>
        </div>
        <Button data-testid="refresh-electronic-payments-btn" onClick={fetchData} size="sm" variant="outline" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {!configured && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm" data-testid="stripe-not-configured-warning">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" /> Stripe is not configured — set STRIPE_API_KEY to see fee/net/deposit details.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <Card className="bg-white border-gray-100">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Total Collected</p>
            <p className="text-xl font-bold mt-1 text-[#003366]" data-testid="ep-total-gross">${totals.gross.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-100">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Stripe Fees</p>
            <p className="text-xl font-bold mt-1 text-rose-600" data-testid="ep-total-fees">-${totals.fees.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-100 col-span-2 lg:col-span-1">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Net Amount</p>
            <p className="text-xl font-bold mt-1 text-emerald-600" data-testid="ep-total-net">${totals.net.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-gray-100 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-gray-400">Loading...</div>
          ) : payments.length === 0 ? (
            <div className="py-16 text-center">
              <Landmark className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">No electronic payments yet</p>
              <p className="text-sm text-gray-400 mt-1">Card / ACH payments collected via Stripe will show up here automatically.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">Date</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">Invoice</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 hidden md:table-cell">Client</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 hidden lg:table-cell">Address / Job</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">Method</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">Amount</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 hidden sm:table-cell">Fee</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">Net</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-4 py-3">Est. Funds Available</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p, i) => (
                    <tr key={`${p.invoice_id}-${p.stripe_payment_intent_id || i}`} data-testid={`ep-row-${i}`} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-600">{(p.payment_date || '').slice(0, 10)}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => navigate(`/invoices`, { state: { openInvoiceId: p.invoice_id } })} className="text-sm text-blue-600 hover:underline flex items-center gap-1" data-testid={`ep-invoice-link-${i}`}>
                          {p.invoice_number}<ExternalLink className="w-3 h-3" />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 hidden md:table-cell">{p.customer_name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 hidden lg:table-cell max-w-[220px] truncate">{p.address || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-0.5 rounded-md font-medium capitalize bg-blue-50 text-blue-700">{METHOD_LABELS[p.method] || p.method}</span>
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">${(p.amount || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-sm text-rose-600 hidden sm:table-cell">{p.fee != null ? `-$${p.fee.toFixed(2)}` : (p.error ? '—' : '…')}</td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-emerald-700">{p.net != null ? `$${p.net.toFixed(2)}` : (p.error ? '—' : '…')}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {p.estimated_deposit_date || (p.error ? <span className="text-gray-400 text-xs">{p.error}</span> : '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      <p className="text-xs text-gray-400">
        "Est. Funds Available" is when Stripe makes the net funds available in your balance — the actual bank deposit
        (payout) usually follows a couple of business days after that, per your Stripe payout schedule.
      </p>
    </div>
  );
}
